/**
 * Centralised imagery registry.
 *
 * One curated image per RouteKey — no reuse of the same photo across
 * multiple hero blocks. Only industrial / institutional / commodity
 * imagery. No "office meeting" stock.
 *
 * All assets are self-hosted under /public/images/<section>/ as
 * responsive WebP files (640 / 960 / 1280 / 1920 widths) generated by
 * /app/scripts/build_image_assets.py. No external CDN is used at runtime.
 * Provenance and licensing: see /app/ASSET_MANIFEST.md.
 */

import type { RouteKey } from "@/lib/routes";

export interface PageImage {
  /** Fallback src — the 1280w rendition. */
  url: string;
  /** Responsive srcset covering 640/960/1280/1920 widths. */
  srcSet: string;
  /**
   * Alt text is intentionally *neutral / descriptive* and not marketing
   * copy. Screen readers hear a description of what the picture depicts,
   * not an invented commercial claim.
   */
  alt: string;
}

const WIDTHS = [640, 960, 1280, 1920];

const L = (section: string, slug: string, alt: string): PageImage => {
  const base = `/images/${section}/${slug}`;
  return {
    url: `${base}-1280w.webp`,
    srcSet: WIDTHS.map((w) => `${base}-${w}w.webp ${w}w`).join(", "),
    alt,
  };
};

export const IMAGERY: Partial<Record<RouteKey, PageImage>> = {
  home: L("home", "port-dusk-cranes",
    "Industrial cargo port at dusk with cranes and container ships."),

  about: L("about", "black-sea-corridor-dusk",
    "Black Sea trade-corridor coastline at dusk."),

  // ---- SERVICES ----
  "services.index": L("services", "services-index-container-ship-aerial",
    "Aerial top-down view of a container ship crossing open sea."),
  "services.trade": L("services", "international-trade-container-terminal",
    "Aerial view of a major container terminal at dawn."),
  "services.sourcing": L("services", "global-sourcing-warehouse",
    "Elevated view of a distribution warehouse with stocked pallet racking."),
  "services.supply": L("services", "commodity-supply-port-night",
    "Cargo port cranes and container terminal at night."),
  "services.consulting": L("services", "commercial-consulting-trading-terminal",
    "Hands typing on a keyboard next to a financial market terminal."),

  // ---- COMMODITIES ----
  "commodities.index": L("commodities", "commodities-index-wheat-field",
    "Golden wheat field at sunset."),
  "commodities.grains": L("commodities", "grains-cereals-wheat-macro",
    "Close-up of harvested wheat grains."),
  "commodities.food": L("commodities", "food-products-bulk-commodities",
    "Bulk processed food commodities for industrial buyers."),
  "commodities.petrochemical": L("commodities", "petrochemical-refinery",
    "Petrochemical refinery complex with towers and stacks under an overcast sky."),

  // ---- COMPLIANCE ----
  "compliance.index": L("compliance", "compliance-index-governance-ledger",
    "Abstract corporate governance and documentation motif."),
  "compliance.corporate": L("compliance", "corporate-information-contract",
    "Contract documents and a coffee cup on a wooden desk."),
  "compliance.kyc": L("compliance", "kyc-signing",
    "Close-up of a hand signing a document with a fountain pen."),
  "compliance.antifraud": L("compliance", "anti-fraud-justice-statue",
    "Bronze statue of Lady Justice holding scales."),

  // ---- RESOURCES ----
  "resources.index": L("resources", "resources-index-warehouse-aisle",
    "Storage aisle in an industrial warehouse."),
  "resources.profile": L("resources", "company-profile-modern-arch",
    "Modern institutional architecture with curved facades."),
  "resources.downloads": L("resources", "downloads-documents",
    "Financial documents, a pocket calculator and a wristwatch on a desk."),
  "resources.trade-docs": L("resources", "trade-documents-paperwork",
    "Detail of stacked paperwork and shipping documentation."),
};

export function imageFor(key: RouteKey): PageImage | undefined {
  return IMAGERY[key];
}
